/**
 * The wire protocol between the client and the game server (BUILD_PLAN section 8, DECISIONS.md
 * #16), validated with Zod on both ends. Messages travel as Colyseus room messages (msgpack); the
 * schemas below are the boundary. Room creation options and match-ticket claims live here too so
 * the API, the matchmaking worker and the room agree on them.
 */
import { z } from 'zod';
import { BotLevelSchema, MatchEventSchema, MatchModeSchema, MatchSetupSchema, MatchStateSchema, SlotSchema, } from '../combat/schemas.js';
import { ArenaIdSchema } from '../arena/registry.js';
import { LoadoutSchema } from '../loadout/schemas.js';
import { MechInputSchema } from '../sim/schemas.js';
import { BalanceOverridesSchema } from '../data/overrides-schema.js';
const int = z.number().int();
const finite = z.number().finite();
export const DeltaSchema = z.lazy(() => z.union([
    z.number(),
    z.string(),
    z.boolean(),
    z.object({ r: z.unknown() }).strict(),
    z.object({ o: z.record(z.string(), DeltaSchema), d: z.array(z.string()).optional() }).strict(),
    z.object({ a: z.record(z.string(), DeltaSchema), n: int.nonnegative() }).strict(),
    z.object({ e: z.record(z.string(), DeltaSchema), k: z.array(int) }).strict(),
]));
// --- client → server ---
/** One input per predicted tick; `input.seq` equals `tick`. */
export const InputMessageSchema = z
    .object({
    t: z.literal('input'),
    tick: int.nonnegative(),
    /** The (fractional) tick the enemy was drawn at when this input was made. */
    viewTick: finite.nonnegative(),
    /** Client clock at send time, echoed back for the round-trip estimate. */
    sentMs: finite.nonnegative(),
    input: MechInputSchema,
})
    .strict();
/** Assets are loaded; the countdown may start. */
export const ReadyMessageSchema = z.object({ t: z.literal('ready') }).strict();
/** The client lost the delta chain or the hash disagreed: send a full snapshot. */
export const ResyncMessageSchema = z
    .object({
    t: z.literal('resync'),
    reason: z.enum(['base', 'hash', 'behind', 'overflow']),
})
    .strict();
export const ClientMessageSchema = z.discriminatedUnion('t', [
    InputMessageSchema,
    ReadyMessageSchema,
    ResyncMessageSchema,
]);
// --- server → client ---
export const PlayerInfoSchema = z
    .object({
    callsign: z.string().min(1).max(32),
    /** Null for the bot seat. */
    userId: z.string().min(1).nullable(),
    bot: BotLevelSchema.nullable(),
})
    .strict();
export const WelcomeMessageSchema = z
    .object({
    t: z.literal('welcome'),
    protocol: int.positive(),
    /** Fingerprint of the arena sidecar the server stepped (Phase 8); the client must match it. */
    arenaHash: int.nonnegative(),
    matchId: z.string().min(1),
    mode: MatchModeSchema,
    slot: SlotSchema,
    setup: MatchSetupSchema,
    players: z.tuple([PlayerInfoSchema, PlayerInfoSchema]),
    tick: int.nonnegative(),
})
    .strict();
/**
 * One authoritative tick: a full state, or a delta against `base` (the previous tick the
 * client received). `hash` verifies the patched state; `events` are that tick's events.
 */
export const SnapshotMessageSchema = z
    .object({
    t: z.literal('snap'),
    tick: int.nonnegative(),
    base: int.nonnegative().nullable(),
    /** The state hash: on every full snapshot and every NET.HASH_EVERY_TICKS ticks. */
    hash: int.nonnegative().nullable(),
    full: MatchStateSchema.optional(),
    delta: DeltaSchema.nullable().optional(),
    events: z.array(MatchEventSchema),
    /** `sentMs` of each seat's newest input, echoed (one message serves both seats). */
    echoMs: z.tuple([finite.nullable(), finite.nullable()]),
    serverMs: finite,
    /** Inputs of each seat that arrived too late since the last snapshot. */
    late: z.tuple([int.nonnegative(), int.nonnegative()]),
})
    .strict();
export const RoomPhaseSchema = z.enum(['waiting', 'live', 'paused', 'ended', 'aborted']);
export const StatusMessageSchema = z
    .object({
    t: z.literal('status'),
    phase: RoomPhaseSchema,
    /** The seat that is reconnecting (paused) or left (ended by forfeit). */
    slot: SlotSchema.nullable(),
    secondsLeft: finite.nonnegative().nullable(),
    reason: z.string().max(200).nullable(),
})
    .strict();
export const ServerMessageSchema = z.discriminatedUnion('t', [
    WelcomeMessageSchema,
    SnapshotMessageSchema,
    StatusMessageSchema,
]);
// --- room creation and tickets ---
export const RoomPlayerSchema = z
    .object({
    userId: z.string().min(1),
    callsign: z.string().min(1).max(32),
    mechId: z.string().min(1),
    /** Already sanitised by the queue; the room sanitises again against the catalog only. */
    loadout: LoadoutSchema.nullable(),
    slot: SlotSchema,
})
    .strict();
export const RoomBotSchema = z
    .object({
    level: z.enum(['easy', 'normal', 'hard']),
    mechId: z.string().min(1),
    slot: SlotSchema,
})
    .strict();
/** What the matchmaking worker hands `matchMaker.createRoom` (validated in `onCreate`). */
export const BattleRoomOptionsSchema = z
    .object({
    matchId: z.string().min(1),
    mode: z.enum(['casual', 'ranked', 'botFill']),
    seed: int.nonnegative(),
    arenaId: ArenaIdSchema,
    players: z.array(RoomPlayerSchema).min(1).max(2),
    bot: RoomBotSchema.nullable(),
    /** The published balance document the match runs under (Phase 7); absent = built-in. */
    balance: z
        .object({ version: int.nonnegative(), overrides: BalanceOverridesSchema })
        .strict()
        .optional(),
    roundSeconds: finite.positive().optional(),
    countdownSeconds: finite.nonnegative().optional(),
})
    .strict();
/** Claims inside a signed match ticket (60 s TTL): who may take which seat of which room. */
export const MatchTicketClaimsSchema = z
    .object({
    sub: z.string().min(1),
    roomId: z.string().min(1),
    matchId: z.string().min(1),
    slot: SlotSchema,
    mode: z.enum(['casual', 'ranked', 'botFill']),
    callsign: z.string().min(1).max(32),
    iat: int.nonnegative(),
    exp: int.nonnegative(),
})
    .strict();
//# sourceMappingURL=schemas.js.map